import React, { useState, useEffect } from "react";
import { ChevronDown, MessageCircle, Star } from "lucide-react";
import PremiumButton from "../ui/PremiumButton";
import { CONTACT, whatsappUrl } from "../../lib/email";

const Hero = () => {
  // Hero slideshow images - prioritizing cinematic exterior/scenery shots
  // Waterfalls, valley, elevation, pool, panoramic views
  const heroImageIds = [
    "1j26Uiwh3VjVo37vp4T_ExaX94m-neApQ",
    "1ALD4pmstRl1sfR_w2JGPxQlLou0xZTSQ",
    "1ub0nrC3VX9cKkEXX-Y4kNsyJ_pYZ3X1u",
    "1puqi3DXxE77GRdHpCAaInz0l3R8NEVGb",
    "1aiEZjSis1jW0XIe0BixHfXrWKqoTQXjU",
    "1vw8874p7FdTRSVHQYSGKOC_CeqBJrw4_"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-advance slideshow every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImageIds.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroImageIds.length]);

  // Generate proxy URL for current image
  const getImageUrl = (id) => {
    return `${process.env.REACT_APP_BACKEND_URL}/api/media/proxy?url=${encodeURIComponent(`https://drive.google.com/uc?export=view&id=${id}`)}`;
  };

  // Optimized WhatsApp pre-filled message for instant booking assistance
  const whatsappMessage = `Hello GlowLAWNS Team,
I would like to check availability and packages for Moonrise Valley.
Please assist me with details.`;

  return (
    <section 
      className="relative flex min-h-[92vh] md:min-h-[95vh] lg:min-h-screen items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background slideshow with cinematic images - ENHANCED FILTERS */}
      {heroImageIds.map((id, index) => (
        <div
          key={id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentIndex ? "opacity-100" : "opacity-0"
          }`}
          style={{
            backgroundImage: `url(${getImageUrl(id)})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            filter: "saturate(1.15) contrast(1.08) brightness(0.95)",
          }}
        />
      ))}

      {/* Premium dark cinematic gradient overlay - Enhanced for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/35 to-black/90 z-[1]" />
      
      {/* Golden accent gradient for warmth */}
      <div className="absolute inset-0 z-[1] opacity-30 bg-gradient-to-tr from-[#C9A227]/20 via-transparent to-transparent" />
      
      {/* Subtle vignette for cinematic feel */}
      <div className="absolute inset-0 z-[2] pointer-events-none" 
           style={{ boxShadow: "inset 0 0 200px 80px rgba(0,0,0,0.5)" }} />

      {/* Main Hero Content */}
      <div 
        className="relative z-10 mx-auto max-w-5xl px-5 flex flex-col items-center justify-center"
        style={{
          paddingTop: "clamp(100px, 12vw, 140px)",
          paddingBottom: "clamp(60px, 8vw, 100px)",
        }}
      >
        <div className="mx-auto max-w-3xl text-center">
          {/* Brand tagline badge - Premium, aspirational */}
          <div 
            className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/40 bg-[#C9A227]/15 px-5 py-2.5 text-[11px] md:text-xs tracking-[0.2em] uppercase text-[#C9A227] backdrop-blur-md mb-8"
            data-testid="hero-location-badge"
          >
            <Star className="w-3.5 h-3.5" fill="currentColor" />
            <span>Kerala's Hidden Forest Sanctuary</span>
          </div>

          {/* PRIMARY HEADLINE - Emotional trigger with location */}
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light leading-[1.08] tracking-wide text-white"
            data-testid="hero-headline"
          >
            Moonrise <span className="text-[#C9A227]">Valley</span>
          </h1>
          
          {/* Sub-headline - Urgency & exclusivity */}
          <p className="mt-4 text-lg md:text-xl lg:text-2xl text-white/90 font-light">
            Where Every Sunrise Feels Like a Private Blessing
          </p>

          {/* SUBTEXT - Aspirational & sensory with FOMO trigger */}
          <p 
            className="mt-6 md:mt-8 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto"
            data-testid="hero-subtext"
          >
            Elevated tree houses. Untouched waterfalls. Complimentary Kerala breakfast.
            <span className="text-white"> Only 4 private cottages available</span> — 
            your escape from the ordinary is just one booking away.
          </p>

          {/* Social Proof Badge */}
          <div className="mt-6 flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
              <div className="flex">
                {[1,2,3,4,5].map((star) => (
                  <Star key={`hero-star-${star}`} className="w-4 h-4 text-[#C9A227]" fill="currentColor" />
                ))}
              </div>
              <span className="text-white/80 text-sm">"Best stay in Kerala" — Our Guests</span>
            </div>
          </div>

          {/* PRIMARY CTA BUTTONS - Visually dominant, tap-friendly */}
          <div 
            className="mt-8 md:mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center"
            data-testid="hero-cta-buttons"
          >
            <PremiumButton 
              href="/contact" 
              variant="primary"
              className="text-base md:text-lg px-8 py-4 min-h-[56px]"
              data-testid="cta-check-availability"
            >
              Check Availability
            </PremiumButton>
            <PremiumButton 
              href="/contact" 
              variant="secondary"
              className="text-base md:text-lg px-8 py-4 min-h-[56px]"
              data-testid="cta-reserve-stay"
            >
              Reserve Your Stay
            </PremiumButton>
          </div>

          {/* WhatsApp Instant Booking Assistance - HIGH PRIORITY */}
          <div className="mt-6">
            <a
              href={whatsappUrl(whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600/20 border border-green-500/30 text-green-400 text-sm md:text-base font-medium hover:bg-green-600/30 hover:border-green-500/50 transition-all duration-300 backdrop-blur-sm"
              data-testid="hero-whatsapp-cta"
            >
              <MessageCircle className="w-5 h-5" />
              Instant Booking Assistance
            </a>
          </div>

          {/* Luxury micro-signals - Trust builders */}
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] md:text-xs text-white/50 uppercase tracking-[0.15em]">
            <span>Private Tree Houses</span>
            <span className="text-[#C9A227]/60">·</span>
            <span>Waterfall Views</span>
            <span className="text-[#C9A227]/60">·</span>
            <span>Free Breakfast</span>
            <span className="text-[#C9A227]/60">·</span>
            <span>Limited Availability</span>
          </div>
        </div>
      </div>

      {/* Slideshow indicators - subtle */}
      <div className="absolute bottom-20 md:bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroImageIds.map((id, index) => (
          <button
            key={`slide-${id}`}
            onClick={() => setCurrentIndex(index)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              index === currentIndex 
                ? "bg-[#C9A227] w-8" 
                : "bg-white/30 w-1.5 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 animate-bounce hidden md:block">
        <ChevronDown className="w-6 h-6 text-white/40" />
      </div>
    </section>
  );
};

export default Hero;
